import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { PROJECTS, INTRO, palettes, ease, BRANDS } from './shared';

/* 12 — DECK
   A sideways deck of full previews you flick through. Two rows deep, so the
   whole body of work is in view without an endless single line. */

// Fisher-Yates. The deck should never open on the same project twice running.
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// These stay out of the first three columns (six cards in two rows) and can only show from the fourth column on.
const LATE_IDS = ['summit', 'onepiecequiz', 'chibidash', 'logo-builder', 'najdar'];
const FRONT_CARDS = 6;

// Swaps any late card out of the first six slots with the first early card behind them.
const keepLateCardsBack = (list) => {
  const out = [...list];
  for (let i = 0; i < Math.min(FRONT_CARDS, out.length); i += 1) {
    if (!LATE_IDS.includes(out[i].id)) continue;
    const j = out.findIndex((p, k) => k >= FRONT_CARDS && !LATE_IDS.includes(p.id));
    if (j < 0) break;
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const Concept12Deck = ({ mode = 'dark', hideFooter = false }) => {
  const c = palettes.deck[mode];
  // brand covers by default; /?covers=screens brings back the screenshots to compare
  const useBrands = typeof window === 'undefined' || !/[?&]covers=screens/.test(window.location.search);
  const rail = useRef(null);
  const [seed, setSeed] = useState(0);

  // reshuffled on every load, and again whenever the shuffle button is hit
  // shuffled every load, except Najdar, which always sits last (far right) — Fahad's rule, 2026-10-16
  const deck = useMemo(() => keepLateCardsBack([
    ...shuffle(PROJECTS.filter((p) => p.id !== 'najdar')),
    ...PROJECTS.filter((p) => p.id === 'najdar')
  ]), [seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const reshuffle = () => {
    setSeed((v) => v + 1);
    rail.current?.scrollTo({ left: 0, behavior: 'smooth' });
  };

  const nudge = (dir) => {
    const el = rail.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 520), behavior: 'smooth' });
  };

  return (
    <div className="dk-page" style={{
      background: c.bg, color: c.ink, minHeight: '100%',
      fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
      display: 'flex', flexDirection: 'column'
    }}>
      <style>{`
        /* one gutter value, used by every band on the page */
        .dk-page { --gut: clamp(20px, 5vw, 72px); }
        .dk-band { padding-left: var(--gut); padding-right: var(--gut); }
        .dk-head { display: flex; align-items: center; gap: clamp(16px, 3vw, 32px); }
        .dk-pic { flex-shrink: 0; width: clamp(72px, 9vw, 128px); height: clamp(72px, 9vw, 128px);
          border-radius: 50%; object-fit: cover; }
        /* a clean grid that fills the width (was a sideways-scrolling rail) */
        .dk-rail { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
          gap: clamp(14px, 2vw, 24px); padding: 4px var(--gut) clamp(32px, 5vw, 64px); }
        .dk-card { width: auto !important; border: none !important; border-radius: 20px !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 10px 30px rgba(0,0,0,0.07) !important;
          transition: transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease !important; }
        .dk-card:hover { transform: translateY(-4px); box-shadow: 0 2px 4px rgba(0,0,0,0.05), 0 22px 50px rgba(0,0,0,0.12) !important; }
        .dk-arrows button[aria-label="Previous"], .dk-arrows button[aria-label="Next"] { display: none; }
        .dk-mail { color: inherit; text-decoration: none; border-bottom: 1px solid currentColor;
          padding-bottom: 2px; opacity: 0.75; transition: opacity 0.2s ease; }
        .dk-mail:hover { opacity: 1; }
        @media (max-width: 760px) {
          .dk-head { flex-direction: column; align-items: flex-start; gap: 16px; }
        }
      `}</style>

      <div className="dk-band" style={{ paddingTop: 'clamp(40px, 7vw, 88px)', paddingBottom: 'clamp(22px, 4vw, 40px)' }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
          style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}
        >
          <div className="dk-head">
            <motion.img
              src="/profilepic.png"
              alt="Fahad Nimer"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease, delay: 0.05 }}
              className="dk-pic"
              style={{ border: `1px solid ${c.line}`, boxShadow: '0 12px 30px rgba(0,0,0,0.22)' }}
            />
            <div>
              <div style={{
                fontSize: '11px', letterSpacing: '0.24em', textTransform: 'uppercase',
                color: c.dim, marginBottom: 12
              }}>
                {INTRO.name}
              </div>
              <h1 style={{
                margin: 0, maxWidth: '19ch', fontSize: 'clamp(1.6rem, 3.8vw, 2.8rem)',
                fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1.1
              }}>
                {INTRO.line}
              </h1>
            </div>
          </div>

          <div className="dk-arrows" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={reshuffle} aria-label="Shuffle the deck" style={{
              height: 42, padding: '0 16px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${c.line}`, background: c.panel, color: c.ink,
              fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7
            }}>
              ⇄ Shuffle
            </button>
            {[-1, 1].map((d) => (
              <button key={d} onClick={() => nudge(d)} aria-label={d < 0 ? 'Previous' : 'Next'} style={{
                width: 42, height: 42, borderRadius: '50%', cursor: 'pointer',
                border: `1px solid ${c.line}`, background: c.panel, color: c.ink, fontSize: 16
              }}>
                {d < 0 ? '←' : '→'}
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="dk-rail" ref={rail}>
        {deck.map((p, i) => (
          <motion.a
            key={`${seed}-${p.id}`}
            href={p.href}
            className="dk-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease, delay: Math.min(i, 8) * 0.05 }}
            whileHover={{ y: -8 }}
            style={{
              textDecoration: 'none', color: c.ink, background: c.panel,
              border: `1px solid ${c.line}`, borderRadius: '16px', overflow: 'hidden',
              boxShadow: '0 12px 30px rgba(0,0,0,0.14)'
            }}
          >
            <div style={{ aspectRatio: '4 / 3', overflow: 'hidden', background: c.bg }}>
              {useBrands && BRANDS[p.id] ? (
                // the project as its brand: its colour, its logo or its name in its own type
                <div style={{ height: '100%', background: BRANDS[p.id].bg, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden' }}>
                  {/* optional photo or site behind the brand, softened so the logo reads on top */}
                  {BRANDS[p.id].bgImage && (
                    <>
                      <img src={BRANDS[p.id].bgImage} alt="" aria-hidden="true" style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                        filter: `blur(${BRANDS[p.id].blur || 0}px)`, transform: BRANDS[p.id].blur ? 'scale(1.08)' : 'none', objectPosition: BRANDS[p.id].bgPos || 'center'
                      }} />
                      <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: BRANDS[p.id].overlay || 'transparent' }} />
                    </>
                  )}
                  <div style={{ position: 'relative', width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
                  {BRANDS[p.id].image ? (
                    <img src={BRANDS[p.id].image} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : BRANDS[p.id].logo ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: BRANDS[p.id].top ? 'flex-start' : 'center', paddingTop: BRANDS[p.id].top ? '7%' : 0, gap: BRANDS[p.id].tight ? '6px' : '10px', paddingLeft: '14px', paddingRight: '14px', boxSizing: 'border-box' }}>
                      <div style={BRANDS[p.id].glass ? { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '16px 22px 14px', borderRadius: '20px', background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.35)', backdropFilter: 'blur(14px) saturate(140%)', WebkitBackdropFilter: 'blur(14px) saturate(140%)', boxShadow: '0 10px 30px rgba(0,0,0,0.18)' } : { display: 'contents' }}>
                      <img src={BRANDS[p.id].logo} alt={p.title} style={{
                        boxShadow: BRANDS[p.id].shadow ? '0 0 0 2px rgba(255,255,255,0.55), 0 10px 24px rgba(0,0,0,0.35)' : 'none', overflow: 'hidden', aspectRatio: BRANDS[p.id].radius ? '1' : 'auto',
                        width: BRANDS[p.id].glass ? (BRANDS[p.id].glassLogo || '78px') : (BRANDS[p.id].size || '40%'), maxHeight: BRANDS[p.id].maxH || '58%', objectFit: 'contain', borderRadius: BRANDS[p.id].radius || 0, display: 'block', mixBlendMode: BRANDS[p.id].blend || 'normal'
                      }} />
                      {BRANDS[p.id].name && (
                        <span style={{ color: BRANDS[p.id].color, fontFamily: BRANDS[p.id].font || 'inherit', fontWeight: BRANDS[p.id].nameWeight || 700,
                          fontStyle: BRANDS[p.id].nameStyle || 'normal', fontSize: 'clamp(1.05rem, 1.8vw, 1.4rem)', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                          {BRANDS[p.id].name}
                        </span>
                      )}
                      {BRANDS[p.id].line && (
                        <span style={{ color: BRANDS[p.id].color, opacity: 0.85, textAlign: 'center',
                          fontSize: BRANDS[p.id].lineCaps ? '9px' : '12.5px', letterSpacing: BRANDS[p.id].lineCaps ? '0.1em' : 0,
                          textTransform: BRANDS[p.id].lineCaps ? 'uppercase' : 'none', fontWeight: BRANDS[p.id].lineCaps ? 600 : 500 }}>
                          {BRANDS[p.id].line}
                        </span>
                      )}
                      </div>
                    </div>
                  ) : (
                    <span style={{
                      color: BRANDS[p.id].color, fontWeight: BRANDS[p.id].weight, letterSpacing: BRANDS[p.id].spacing,
                      fontFamily: BRANDS[p.id].font || 'inherit', fontSize: 'clamp(1.4rem, 2.6vw, 2rem)', padding: '0 16px', textAlign: 'center', lineHeight: 1.1
                    }}>{BRANDS[p.id].word}
                      {BRANDS[p.id].line && <span style={{ display: 'block', marginTop: '8px', fontSize: '12.5px', fontWeight: 500, letterSpacing: 0, opacity: 0.85 }}>{BRANDS[p.id].line}</span>}
                    </span>
                  )}
                  </div>
                </div>
              ) : p.cover ? (
                <img src={p.cover} alt={`${p.title} preview`} loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <div style={{ display: 'grid', placeItems: 'center', height: '100%', fontSize: '3rem', color: c.dim }}>
                  {p.title.charAt(0)}
                </div>
              )}
            </div>
            <div style={{ padding: '14px 18px 18px' }}>
              {/* meta wraps to two lines on narrow cards; line-height keeps that tidy */}
              <div style={{ fontSize: '10.5px', letterSpacing: '0.14em', lineHeight: 1.45, textTransform: 'uppercase', color: c.dim }}>
                {p.n} · {p.category}
              </div>
              <div style={{ fontSize: '1.08rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.3, marginTop: 8 }}>
                {p.title}
              </div>
              {/* two lines reserved, so every card in a row ends at the same height */}
              <div style={{ fontSize: '13px', color: c.dim, marginTop: 6, lineHeight: 1.5, minHeight: 'calc(1.5em * 2)' }}>
                {p.tagline}
              </div>
            </div>
          </motion.a>
        ))}
      </div>

      {/* Skipped on the real site — Layout already renders this exact footer. */}
      {!hideFooter && <footer className="dk-band" style={{
        marginTop: 'auto',
        borderTop: `1px solid ${c.line}`,
        paddingTop: 'clamp(24px, 4vw, 40px)',
        paddingBottom: 'clamp(32px, 5vw, 52px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '16px', flexWrap: 'wrap'
      }}>
        <span style={{ fontSize: '13px', color: c.dim }}>
          © 2026 Fahad Nimer. Building with truth and clarity.
        </span>
        <a
          href={`mailto:${INTRO.email}`}
          className="dk-mail"
          style={{ fontSize: '13px', color: c.dim, letterSpacing: '0.01em' }}
        >
          {INTRO.email}
        </a>
      </footer>}
    </div>
  );
};

export default Concept12Deck;
